import React, { useState } from 'react';
import { Building2, MapPin, Users, AlertCircle, Plus, Trash2 } from 'lucide-react';
import DataTable from '../../../../components/ui/DataTable';
import { useSites, useSiteActions } from '../../hooks/useSites';
import { Site } from '../../../../types/organisation';
import OrganisationNavigation from '../OrganisationNavigation';
import SiteDetailForm from './SiteDetailForm';

const SiteManagement: React.FC = () => {
  const { sites, loading, refresh } = useSites();
  const { deleteSite } = useSiteActions();
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Site[]>([]);

  const columns = [
    { key: 'siteCode', title: 'Code', sortable: true },
    {
      key: 'siteName',
      title: 'Nom du site',
      sortable: true,
      render: (value: string, item: Site) => (
        <div>
          <div className="font-medium">{value}</div>
          {item.isMainSite && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Principal
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'siteType',
      title: 'Type',
      render: (value: unknown) => value?.name || value,
    },
    { key: 'city', title: 'Ville' },
    {
      key: 'employeeCount',
      title: 'Employés',
      render: (value: number) => value || 0,
    },
    {
      key: 'isActive',
      title: 'Statut',
      render: (value: boolean) => value ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Actif</span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Inactif</span>
      ),
    },
  ];

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return;
    if (!window.confirm(`Supprimer ${selectedRows.length} site(s) ?`)) return;
    try {
      await Promise.all(selectedRows.map(s => deleteSite(s.id)));
      setSelectedRows([]);
      await refresh();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleClose = async () => {
    setSelectedId(undefined);
    await refresh();
  };

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-2 md:p-3">
              <Building2 className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">{sites.length}</div>
              <div className="text-xs md:text-sm text-gray-600">Sites total</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-emerald-500 rounded-md p-2 md:p-3">
              <MapPin className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {sites.filter(s => s.isActive).length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Sites actifs</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-teal-500 rounded-md p-2 md:p-3">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {sites.reduce((sum, s) => sum + (s.employeeCount || 0), 0)}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Employés total</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-lime-500 rounded-md p-2 md:p-3">
              <AlertCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {sites.filter(s => !s.isActive).length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Sites inactifs</div>
            </div>
          </div>
        </div>
      </div>

      {/* En-tête + Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Sites</h1>
            <p className="text-gray-600">Gérez vos sites industriels et leurs informations</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRows.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              <span>Supprimer ({selectedRows.length})</span>
            </button>
          )}
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Nouveau site</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <DataTable
          data={sites}
          columns={columns}
          loading={loading}
          selectable={true}
          onSelectionChange={setSelectedRows}
          onRowDoubleClick={(site) => setSelectedId(site.id)}
          emptyMessage="Aucun site trouvé"
        />
      </div>

      {/* Formulaire de détail */}
      {selectedId !== undefined && (
        <SiteDetailForm
          siteId={selectedId}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default SiteManagement;
